import React, { lazy, Suspense } from "react";
import ProtectedRoutes from "./auth/ProtectedRoutes";
import { authRoles } from "./auth/authRoles";
import MatxLayout from "./components/MatxLayout/MatxLayout";
import SpinnerBasic from "./components/loaders/SpinnerBasic/SpinnerBasic";

// Carregamento dinâmico dos componentes com lazy para evitar sobrecarga no build
const GeneralReportView = lazy(() => import("./views/general-view/main-view/Analytics"));
const AppEchart = lazy(() => import("./views/charts/echarts/AppEchart"));
const EventsListing = lazy(() => import("./views/events/listing/EventsListing"));
const EventForm = lazy(() => import("./views/events/form/EventForm"));
const EventPeriodsListing = lazy(() => import("./views/event-periods/listing/EventPeriodsListing"));
const EventPeriodForm = lazy(() => import("./views/event-periods/form/EventPeriodForm"));
const UsersListing = lazy(() => import("./views/users/listing/UsersListing"));
const UserForm = lazy(() => import("./views/users/form/UserForm"));
const EventUsersListing = lazy(() => import("./views/event-users/listing/EventUsersListing"));
const EventUsersForm = lazy(() => import("./views/event-users/form/EventUsersForm"));
const ProductsListing = lazy(() => import("./views/products/listing/ProductsListing"));
const ProductForm = lazy(() => import("./views/products/form/ProductForm"));
const CategoriesListing = lazy(() => import("./views/categories/listing/CategoriesListing"));
const CategoryForm = lazy(() => import("./views/categories/form/CategoryForm"));
const TerminalForm = lazy(() => import("./views/terminals/form/TerminalForm"));
const TerminalsListing = lazy(() => import("./views/terminals/listing/TerminalsListing"));
const SalesStandUserForm = lazy(() => import("./views/sale-stands-users/form/SalesStandUserForm"));
const SaleStandsUsersListing = lazy(() =>
  import("./views/sale-stands-users/listing/SaleStandsUsersListing")
);
const AllSalesStands = lazy(() => import("./views/sale-stands/listing/SaleStandsListing"));
const SaleStandForm = lazy(() => import("./views/sale-stands/form/SaleStandsForm"));
const OrdersListing = lazy(() => import("./views/orders/listing/OrdersListing"));
const SingleOrder = lazy(() => import("./views/orders/details/SingleOrder"));
const Login = lazy(() => import("./views/sessions/Login"));
const JwtRegister = lazy(() => import("./views/sessions/JwtRegister"));
const ForgotPassword = lazy(() => import("./views/sessions/ForgotPassword"));
const NotFound = lazy(() => import("./views/sessions/NotFound"));
const SingleEventSummary = lazy(() =>
  import("./views/reports/synthetic/details/SingleSyntheticReport")
);
const SyntheticListing = lazy(() => import("./views/reports/synthetic/listing/SyntheticListing"));
const AllRefunds = lazy(() => import("./views/refunds/listing/RefundsListing"));
const CashlessListing = lazy(() => import("./views/cashless/listing/CashlessListing"));
const SingleCashlessInfo = lazy(() => import("./views/cashless/details/CashlessDetails"));
const CustomersListing = lazy(() => import("./views/customers/listing/CustomersListing"));
const CustomerForm = lazy(() => import("./views/customers/form/CustomerForm"));
const DocumentationListing = lazy(() =>
  import("./views/documentation-helper/listing/DocumentationListing")
);
const RegistrationOrderPage = lazy(() =>
  import("./views/documentation-helper/listing/RegistrationOrderPage")
);
const UserManagementPage = lazy(() =>
  import("./views/documentation-helper/listing/UserManagementPage")
);
const ReportsAnalyticsPage = lazy(() =>
  import("./views/documentation-helper/listing/ReportsAnalyticsPage")
);
const IncomeExpensesListing = lazy(() =>
  import("./views/income-expenses/listing/IncomeExpensesListing")
);
const FinancialForm = lazy(() => import("./views/income-expenses/form/IncomesExpensesForm"));
const GraphsListing = lazy(() => import("./views/reports/graphs/listing/GraphsListing"));
const GraphDetails = lazy(() => import("./views/reports/graphs/details/GraphDetails"));
const Forbidden = lazy(() => import("./views/sessions/Forbidden"));
const Checkout = lazy(() => import("./views/refunds/template/Checkout"));
const RefundForm = lazy(() => import("./views/refunds/RefundForm"));
const AnalyticalReportListing = lazy(() =>
  import("./views/reports/analytical/listing/AnalyticalReportListing")
);
const SingleAnalyticalReport = lazy(() =>
  import("./views/reports/analytical/details/SingleAnalyticalReport")
);
const TransactionsListing = lazy(() =>
  import("./views/reports/transactions/listing/TransactionsListing")
);
const TransactionsDetails = lazy(() =>
  import("./views/reports/transactions/details/TransactionsDetails")
);

const AddressListing = lazy(() => import("./views/addresses/listing/AddressListing"));
const AddressForm = lazy(() => import("./views/addresses/form/AddressForm"));
const CashDropsListing = lazy(() => import("./views/cash-drops/listing/CashDropsListing"));

// Função de fallback enquanto os componentes carregam
const LoadingFallback = (
  <div className="flex justify-center items-center h-full">
    <SpinnerBasic></SpinnerBasic>
  </div>
);

const routes = [
  {
    element: (
      <ProtectedRoutes>
        <MatxLayout />
      </ProtectedRoutes>
    ),
    children: [
      {
        path: "/",
        element: (
          <Suspense fallback={LoadingFallback}>
            <GeneralReportView />
          </Suspense>
        ),
        auth: authRoles.public
      },
      {
        path: "/charts/echarts",
        element: (
          <Suspense fallback={LoadingFallback}>
            <AppEchart />
          </Suspense>
        ),
        auth: authRoles.public
      },
      {
        path: "/events",
        element: (
          <Suspense fallback={LoadingFallback}>
            <EventsListing />
          </Suspense>
        ),
        auth: authRoles.public
      },
      {
        path: "/events/:eventId",
        element: (
          <Suspense fallback={LoadingFallback}>
            <EventForm />
          </Suspense>
        ),
        auth: authRoles.admin
      },
      {
        path: "/event-periods",
        element: (
          <Suspense fallback={LoadingFallback}>
            <EventPeriodsListing />
          </Suspense>
        ),
        auth: authRoles.public
      },
      {
        path: "/event-periods/create",
        element: (
          <Suspense fallback={LoadingFallback}>
            <EventPeriodForm />
          </Suspense>
        ),
        auth: authRoles.public
      },
      {
        path: "/event-periods/:eventPeriodId",
        element: (
          <Suspense fallback={LoadingFallback}>
            <EventPeriodForm />
          </Suspense>
        ),
        auth: authRoles.public
      },
      {
        path: "/users",
        element: (
          <Suspense fallback={LoadingFallback}>
            <UsersListing />
          </Suspense>
        ),
        auth: authRoles.public
      },
      {
        path: "/users/:userId",
        element: (
          <Suspense fallback={LoadingFallback}>
            <UserForm />
          </Suspense>
        ),
        auth: authRoles.public
      },
      {
        path: "/event-users",
        element: (
          <Suspense fallback={LoadingFallback}>
            <EventUsersListing />
          </Suspense>
        ),
        auth: authRoles.public
      },
      {
        path: "/event-user/sync-users",
        element: (
          <Suspense fallback={LoadingFallback}>
            <EventUsersForm />
          </Suspense>
        ),
        auth: authRoles.public
      },
      {
        path: "/products",
        element: (
          <Suspense fallback={LoadingFallback}>
            <ProductsListing />
          </Suspense>
        ),
        auth: authRoles.public
      },
      {
        path: "/products/:productId",
        element: (
          <Suspense fallback={LoadingFallback}>
            <ProductForm />
          </Suspense>
        ),
        auth: authRoles.public
      },
      {
        path: "/categories",
        element: (
          <Suspense fallback={LoadingFallback}>
            <CategoriesListing />
          </Suspense>
        ),
        auth: authRoles.public
      },
      {
        path: "/categories/:categoryId",
        element: (
          <Suspense fallback={LoadingFallback}>
            <CategoryForm />
          </Suspense>
        ),
        auth: authRoles.admin
      },
      {
        path: "/addresses",
        element: (
          <Suspense fallback={LoadingFallback}>
            <AddressListing />
          </Suspense>
        ),
        auth: authRoles.admin
      },
      {
        path: "/addresses/:addressId",
        element: (
          <Suspense fallback={LoadingFallback}>
            <AddressForm />
          </Suspense>
        ),
        auth: authRoles.admin
      },
      {
        path: "/terminals",
        element: (
          <Suspense fallback={LoadingFallback}>
            <TerminalsListing />
          </Suspense>
        ),
        auth: authRoles.public
      },
      {
        path: "/terminals/:terminalId",
        element: (
          <Suspense fallback={LoadingFallback}>
            <TerminalForm />
          </Suspense>
        ),
        auth: authRoles.admin
      },
      {
        path: "/sale-stand-user",
        element: (
          <Suspense fallback={LoadingFallback}>
            <SaleStandsUsersListing />
          </Suspense>
        ),
        auth: authRoles.public
      },
      {
        path: "/sale-stand-user/sync-users",
        element: (
          <Suspense fallback={LoadingFallback}>
            <SalesStandUserForm />
          </Suspense>
        ),
        auth: authRoles.public
      },
      {
        path: "/sale-stands",
        element: (
          <Suspense fallback={LoadingFallback}>
            <AllSalesStands />
          </Suspense>
        ),
        auth: authRoles.public
      },
      {
        path: "/sale-stands/:saleStandId",
        element: (
          <Suspense fallback={LoadingFallback}>
            <SaleStandForm />
          </Suspense>
        ),
        auth: authRoles.public
      },
      {
        path: "/documentation-helper",
        element: (
          <Suspense fallback={LoadingFallback}>
            <DocumentationListing />
          </Suspense>
        )
      },
      {
        path: "/documentation-helper/registration-order",
        element: (
          <Suspense fallback={LoadingFallback}>
            <RegistrationOrderPage />
          </Suspense>
        )
      },
      {
        path: "/documentation-helper/user-management",
        element: (
          <Suspense fallback={LoadingFallback}>
            <UserManagementPage />
          </Suspense>
        )
      },
      {
        path: "/documentation-helper/reports-analytics",
        element: (
          <Suspense fallback={LoadingFallback}>
            <ReportsAnalyticsPage />
          </Suspense>
        )
      },
      {
        path: "/orders",
        element: (
          <Suspense fallback={LoadingFallback}>
            <OrdersListing />
          </Suspense>
        ),
        auth: authRoles.public
      },
      {
        path: "/orders/:orderId",
        element: (
          <Suspense fallback={LoadingFallback}>
            <SingleOrder />
          </Suspense>
        ),
        auth: authRoles.public
      },
      {
        path: "/cashless",
        element: (
          <Suspense fallback={LoadingFallback}>
            <CashlessListing />
          </Suspense>
        )
      },
      {
        path: "/cashless/:cashlessId",
        element: (
          <Suspense fallback={LoadingFallback}>
            <SingleCashlessInfo />
          </Suspense>
        )
      },
      {
        path: "/reports/summary",
        element: (
          <Suspense fallback={LoadingFallback}>
            <SyntheticListing />
          </Suspense>
        )
      },
      {
        path: "/reports/summary/:eventId",
        element: (
          <Suspense fallback={LoadingFallback}>
            <SingleEventSummary />
          </Suspense>
        )
      },
      {
        path: "/reports/graphs",
        element: (
          <Suspense fallback={LoadingFallback}>
            <GraphsListing />
          </Suspense>
        )
      },
      {
        path: "/reports/graphs/:eventId",
        element: (
          <Suspense fallback={LoadingFallback}>
            <GraphDetails />
          </Suspense>
        )
      },
      {
        path: "/reports/general",
        element: (
          <Suspense fallback={LoadingFallback}>
            <AnalyticalReportListing />
          </Suspense>
        )
      },
      {
        path: "/reports/general/:eventId",
        element: (
          <Suspense fallback={LoadingFallback}>
            <SingleAnalyticalReport />
          </Suspense>
        )
      },
      {
        path: "/reports/transactions",
        element: (
          <Suspense fallback={LoadingFallback}>
            <TransactionsListing />
          </Suspense>
        )
      },
      {
        path: "/reports/transactions/:eventId",
        element: (
          <Suspense fallback={LoadingFallback}>
            <TransactionsDetails />
          </Suspense>
        )
      },
      {
        path: "/customer-cashless-refunds",
        element: (
          <Suspense fallback={LoadingFallback}>
            <AllRefunds />
          </Suspense>
        ),
        auth: authRoles.admin
      },
      {
        path: "/customer-cashless-refunds/:refundId",
        element: (
          <Suspense fallback={LoadingFallback}>
            <RefundForm />
          </Suspense>
        ),
        auth: authRoles.admin
      },
      {
        path: "/customers",
        element: (
          <Suspense fallback={LoadingFallback}>
            <CustomersListing />
          </Suspense>
        )
      },
      {
        path: "/customers/create",
        element: (
          <Suspense fallback={LoadingFallback}>
            <CustomerForm />
          </Suspense>
        )
      },
      {
        path: "/customers/:customerId",
        element: (
          <Suspense fallback={LoadingFallback}>
            <CustomerForm />
          </Suspense>
        )
      },
      // {
      //   path: "/incomes-and-expenses",
      //   element: (
      //     <Suspense fallback={LoadingFallback}>
      //       <IncomeExpensesListing />
      //     </Suspense>
      //   )
      // },
      // {
      //   path: "/incomes-and-expenses/:formId",
      //   element: (
      //     <Suspense fallback={LoadingFallback}>
      //       <FinancialForm />
      //     </Suspense>
      //   )
      // },
      {
        path: "/cash-drops",
        element: (
          <Suspense fallback={LoadingFallback}>
            <CashDropsListing />
          </Suspense>
        )
      }
    ]
  },
  {
    path: "/login",
    element: (
      <Suspense fallback={LoadingFallback}>
        <Login />
      </Suspense>
    )
  },
  {
    path: "/signup",
    element: (
      <Suspense fallback={LoadingFallback}>
        <JwtRegister />
      </Suspense>
    )
  },
  {
    path: "/forgot-password",
    element: (
      <Suspense fallback={LoadingFallback}>
        <ForgotPassword />
      </Suspense>
    )
  },
  {
    path: "*",
    element: (
      <Suspense fallback={LoadingFallback}>
        <NotFound />
      </Suspense>
    )
  },
  {
    path: "/forbidden",
    element: (
      <Suspense fallback={LoadingFallback}>
        <Forbidden />
      </Suspense>
    )
  },
  {
    path: "/reembolso/forms",
    element: (
      <Suspense fallback={LoadingFallback}>
        <Checkout />
      </Suspense>
    )
  }
];

export default routes;
